import React from "react";
import styled, { css, keyframes } from "styled-components";
import { ITheme } from "../Themes/ThemeTypes";

export type AvatarRingStatus = "pending" | "failed";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  border-radius: 9999px;
`;

const StatusRing = styled.span<{ theme: ITheme; $status: AvatarRingStatus }>`
  position: absolute;
  inset: -3px;
  border-radius: 9999px;
  pointer-events: none;
  ${(props) =>
    props.$status === "failed"
      ? css`
          border: 3px solid rgb(${props.theme.colors.error});
        `
      : css`
          background: conic-gradient(
            from 0deg,
            transparent 0deg,
            rgb(${props.theme.colors.primary}) 300deg,
            transparent 360deg
          );
          -webkit-mask: radial-gradient(
            farthest-side,
            transparent calc(100% - 3px),
            #000 calc(100% - 3px)
          );
          mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px));
          animation: ${spin} 1s linear infinite;
          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `}
`;

const Circle = styled.div<{ theme: ITheme; $size: number; $clickable: boolean }>`
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border: none;
  padding: 0;
  border-radius: 9999px;
  background: rgb(${(props) => props.theme.colors.primary});
  color: rgb(${(props) => props.theme.colors.on_primary});
  font-family: ${(props) => props.theme.base.typography.font};
  font-size: ${(props) => Math.round(props.$size * 0.4)}px;
  font-weight: ${(props) => props.theme.base.typography.title.medium.font_weight};
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  user-select: none;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: box-shadow ${(props) => props.theme.base.motion.duration.short4}ms
    ${(props) => props.theme.base.motion.easing.standard};

  ${(props) =>
    props.$clickable && `&:hover { box-shadow: ${props.theme.base.elevation.level1_shadow}; }`}
`;

const getInitials = (value: string): string => {
  const parts = value
    .trim()
    .split(/[\s._@-]+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!.charAt(0);
  return parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0);
};

interface AvatarProps {
  name?: string;
  size?: number;
  onClick?: () => void;
  ariaLabel?: string;
  ringStatus?: AvatarRingStatus;
}

const Avatar: React.FC<AvatarProps> = ({
  name = "",
  size = 40,
  onClick,
  ariaLabel,
  ringStatus,
}) => (
  <Wrapper>
    <Circle
      as={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      $size={size}
      $clickable={onClick !== undefined}
      onClick={onClick}
      aria-label={onClick ? ariaLabel : undefined}
      aria-hidden={onClick ? undefined : true}
    >
      {getInitials(name) || "?"}
    </Circle>
    {ringStatus && <StatusRing $status={ringStatus} />}
  </Wrapper>
);

export default Avatar;
